class LpNightCreamProduct {
  constructor(section, config) {
    this.section = section;
    this.config = config || {};
    this.sectionId = config.sectionId || section.dataset.sectionId;
    this.moneySymbol = config.moneySymbol || '$';
    this.priceMode = config.priceMode || config.price_mode || 'price_each';
    this.variantPicker = config.variantPicker || 'bundle';
    this.requireVariantSelection = config.requireVariantSelection === true;
    this.hideVariantsOnSelect = config.hideVariantsOnSelect === true;
    this.pickerCollapsed = false;
    this.hasSellingPlans = config.hasSellingPlans === true;
    this.purchaseType = config.defaultPurchaseType !== 'onetime' ? 'subscription' : 'onetime';
    this.selectedPlanId = null;
    this.armedUpsellVariantId = null;
    this.currentUpsellConfig = null;
    this.variantPlans = this.#readVariantPlans();
    this.#initDeliveryGeo();
    this.#initGallery();
    this.#initBundleSelection();
    this.#initUpsell();
    this.#initSubscription();
    this.#initAddToCart();
    this.#initTestimonialVideos();
    this.#initQuickLinks();
    this.#initTestimonialsSlider();
    this.#initStickyAtc();
  }

  #readVariantPlans() {
    const el = document.getElementById('LpVariantPlans-' + this.sectionId);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  #formatMoney(cents, round) {
    if (round) return this.moneySymbol + Math.floor(cents / 100);
    return this.moneySymbol + (cents / 100).toFixed(2);
  }

  #getCheckedBundleRadio() {
    return this.section.querySelector('.lp-bundle-radio:checked');
  }

  #getFirstBundleRadio() {
    return this.section.querySelector('.lp-bundle-radio');
  }

  #getFrequencySourceRadio() {
    return this.#getCheckedBundleRadio() || this.#getFirstBundleRadio();
  }

  #getCartQuantity(radio) {
    if (this.variantPicker === 'dynamic') return 1;
    // Purchase card options each map to their own variant (see lp-purchase-card.liquid), so
    // data-quantity there is display-only — the cart line quantity is always 1.
    if (radio.closest('[data-lp-purchase-card]')) return 1;
    return parseInt(radio.dataset.quantity, 10) || 1;
  }

  #getLineTotals(active, radio) {
    // Dynamic picker and purchase card options each map to their own variant, whose
    // price/compare-at already reflect the full pack — no per-unit multiplication needed.
    if (this.variantPicker === 'dynamic' || radio.closest('[data-lp-purchase-card]')) {
      return { original: active.compareAt, discounted: active.price };
    }
    const qty = parseInt(radio.dataset.quantity, 10) || 1;
    return {
      original: active.compareEach * qty,
      discounted: active.priceEach * qty,
    };
  }

  #getVariantPricing(variantId) {
    if (!this.variantPlans || !this.variantPlans.variants) return null;
    return this.variantPlans.variants[String(variantId)] || null;
  }

  #getPlan(plans, planId, defaultSubFrequency) {
    if (!plans || !plans.length) return null;
    if (planId) {
      const match = plans.find((plan) => String(plan.id) === String(planId));
      if (match) return match;
    }
    if (this.#hasVariantDefaultFrequency(defaultSubFrequency)) {
      return this.#getDefaultPlan(plans, defaultSubFrequency);
    }
    return plans[0] || null;
  }

  #getPurchaseCardUnitPrices(radio) {
    // OTP price/compare and the sub total are the variant's own full pack price — used by the
    // main Add to Cart button, which always shows the full amount. subEach/subCompareEach are the
    // same basis divided down to a per-bottle figure, used only by the subscribe panel's own display.
    let subEach = parseInt(radio.dataset.subPrice, 10) || parseInt(radio.dataset.discountedPrice, 10) || 0;
    let subTotal = parseInt(radio.dataset.subPriceTotal, 10) || subEach;
    let priceEach = parseInt(radio.dataset.price, 10) || parseInt(radio.dataset.originalPrice, 10) || 0;
    let compareEach = parseInt(radio.dataset.comparePrice, 10) || priceEach;
    let subCompareEach = parseInt(radio.dataset.subComparePrice, 10) || compareEach;
    let planId = radio.dataset.sellingPlanId || null;

    // Selling plan id only — used when adding subscription to cart.
    const pricing = this.#getVariantPricing(radio.dataset.variantId);
    if (pricing && pricing.plans && pricing.plans.length) {
      const plan = this.#getPlan(
        pricing.plans,
        this.selectedPlanId || planId,
        pricing.defaultSubFrequency
      );
      if (plan) planId = plan.id;
    }

    return { subEach, subTotal, priceEach, compareEach, subCompareEach, planId };
  }

  #getActivePricing(radio) {
    const inPurchaseCard = !!radio.closest('[data-lp-purchase-card]');
    if (inPurchaseCard) {
      const { subTotal, priceEach, compareEach, planId } = this.#getPurchaseCardUnitPrices(radio);
      const isOnetime = this.purchaseType === 'onetime';
      // The Add to Cart button always shows the full pack total (never the per-bottle figure).
      const unit = isOnetime ? priceEach : subTotal;
      return {
        priceEach: unit,
        compareEach: compareEach || unit,
        price: unit,
        compareAt: compareEach || unit,
        planId: isOnetime ? null : planId,
      };
    }

    const pricing = this.#getVariantPricing(radio.dataset.variantId);
    if (!pricing) {
      const compareEach = parseInt(radio.dataset.originalPrice, 10) || 0;
      const isOnetime = this.purchaseType === 'onetime';
      const priceEach = isOnetime ? compareEach : parseInt(radio.dataset.discountedPrice, 10) || 0;
      return {
        priceEach,
        compareEach,
        price: priceEach,
        compareAt: compareEach,
        planId: isOnetime ? null : radio.dataset.sellingPlanId || null,
      };
    }

    if (this.purchaseType === 'subscription' && pricing.plans && pricing.plans.length > 0) {
      const planId = radio.checked ? this.selectedPlanId : null;
      const plan = this.#getPlan(pricing.plans, planId, pricing.defaultSubFrequency);
      if (plan) {
        return {
          priceEach: plan.priceEach,
          compareEach: pricing.otp.compareEach,
          price: plan.price,
          compareAt: pricing.otp.compareAt,
          planId: plan.id,
        };
      }
    }

    return {
      priceEach: pricing.otp.priceEach,
      compareEach: pricing.otp.compareEach,
      price: pricing.otp.price,
      compareAt: pricing.otp.compareAt,
      planId: null,
    };
  }

  #updateBundleCardPrices(radio) {
    const item = radio.closest('.lp-bundle-item');
    const purchaseCard = radio.closest('[data-lp-purchase-card]');
    if (!item && !purchaseCard) return;

    const pricing = this.#getActivePricing(radio);

    if (item) {
      const compareEl = item.querySelector('[data-lp-compare-price]');
      const priceEl = item.querySelector('[data-lp-current-price]');
      const suffixEl = item.querySelector('[data-lp-price-suffix]');
      const discountEl = item.querySelector('[data-lp-discount]');
      const dailyEl = item.querySelector('.lp-bundle-price-daily');
      if (!compareEl || !priceEl) return;

      const showEach = this.priceMode === 'price_each';
      const compareVal = showEach ? pricing.compareEach : pricing.compareAt;
      const priceVal = showEach ? pricing.priceEach : pricing.price;

      compareEl.textContent = this.#formatMoney(compareVal, true);
      priceEl.textContent = this.#formatMoney(priceVal, false);
      compareEl.classList.toggle('hidden', compareVal <= priceVal);
      if (suffixEl) suffixEl.classList.toggle('hidden', !showEach);

      if (discountEl) {
        discountEl.classList.toggle('hidden', compareVal <= priceVal);
        const discountTextVal = radio.dataset.discountText;
        const discountAmountVal = compareVal - priceVal;
        const discountPercentageVal = ((discountAmountVal / compareVal) * 100).toFixed(0) + '%';
        discountEl.textContent = discountTextVal
          .replace('[discount]', this.#formatMoney(discountAmountVal, false))
          .replace('[discount_percentage]', discountPercentageVal);
      }

      if (dailyEl) {
        // Daily price is simply the displayed price divided by a 30-day supply.
        dailyEl.textContent = (dailyEl.getAttribute('data-text') || '').replaceAll(
          '[daily_price]',
          this.#formatMoney(Math.floor(priceVal / 30), false)
        );
      }
    }

    if (purchaseCard) {
      const qty = radio.dataset.quantity;
      const { subEach, priceEach, subCompareEach } = this.#getPurchaseCardUnitPrices(radio);

      // Subscribe panel always shows subscription unit price — never OTP.
      const subPriceEl = purchaseCard.querySelector(
        `.lp-plan-panel-group--subscribe .lp-plan-panel[data-qty="${qty}"] [data-lp-current-price]`
      );
      const subCompareEl = purchaseCard.querySelector(
        `.lp-plan-panel-group--subscribe .lp-plan-panel[data-qty="${qty}"] [data-lp-compare-price]`
      );
      if (subPriceEl) {
        subPriceEl.innerHTML = this.#formatMoney(subEach, false) + '<small>/ea</small>';
      }
      if (subCompareEl) {
        subCompareEl.textContent = this.#formatMoney(subCompareEach, true);
        subCompareEl.classList.toggle('hidden', subCompareEach <= subEach);
      }

      // OTP panel always shows product line total — never subscription.
      const otpPriceEl = purchaseCard.querySelector(
        `.lp-plan-panel-group--onetime .lp-plan-panel[data-qty="${qty}"] [data-lp-price], .lp-plan-panel-group--onetime .lp-plan-panel[data-qty="${qty}"] .lp-plan-panel__onetime-price`
      );
      if (otpPriceEl) {
        // priceEach already reflects the full price of this option's own variant.
        otpPriceEl.textContent = this.#formatMoney(priceEach, false);
      }
    }
  }

  #updateAllBundleCardPrices() {
    this.section.querySelectorAll('.lp-bundle-radio').forEach((radio) => {
      this.#updateBundleCardPrices(radio);
    });
  }

  #hasVariantDefaultFrequency(defaultSubFrequency) {
    return defaultSubFrequency != null && String(defaultSubFrequency).trim() !== '';
  }

  #getDefaultPlan(plans, defaultSubFrequency) {
    const defaultFreq = (defaultSubFrequency || '').toLowerCase().trim();
    if (!defaultFreq) return plans[0] || null;
    return plans.find((plan) => (plan.name || '').toLowerCase().includes(defaultFreq)) || plans[0] || null;
  }

  #renderFrequencySelect(radio) {
    const select = this.section.querySelector('[data-lp-frequency-select]');
    if (!select) return;

    const pricing = this.#getVariantPricing(radio.dataset.variantId);
    const plans = pricing && pricing.plans ? pricing.plans : [];
    select.innerHTML = '';

    if (plans.length === 0) {
      select.disabled = true;
      this.#setSubscriptionExtrasVisible(false);
      return;
    }

    this.#setSubscriptionExtrasVisible(this.purchaseType === 'subscription');

    select.disabled = false;
    for (const plan of plans) {
      const option = document.createElement('option');
      option.value = plan.id;
      option.textContent = (plan.name || '').replace(/^Deliver every\s+/i, '');
      select.appendChild(option);
    }

    const planIds = plans.map((plan) => String(plan.id));
    if (!this.selectedPlanId || !planIds.includes(String(this.selectedPlanId))) {
      const defaultPlan = this.#hasVariantDefaultFrequency(pricing?.defaultSubFrequency)
        ? this.#getDefaultPlan(plans, pricing.defaultSubFrequency)
        : null;
      this.selectedPlanId = defaultPlan ? defaultPlan.id : planIds[0] || null;
    }
    if (this.selectedPlanId) select.value = this.selectedPlanId;
  }

  #onVariantChange(radio) {
    if (!radio) return;

    const pricing = this.#getVariantPricing(radio.dataset.variantId);
    const plans = pricing && pricing.plans ? pricing.plans : [];
    const planIds = plans.map((plan) => String(plan.id));

    if (radio.dataset.subPlanIndex != null) {
  // Correct cadence based on selected bottle supply:
  // 1 Bottle = 30 days
  // 3 Bottles = 90 days
  // 6 Bottles = 180 days
  const qty = parseInt(radio.dataset.quantity, 10) || 1;
  const targetDays = qty * 30;

  const plan = plans.find((item) => {
    const name = (item.name || '').toLowerCase();
    return name.includes(targetDays + ' days');
  }) || plans[0];

  this.selectedPlanId = plan ? plan.id : null;
}else if (this.#hasVariantDefaultFrequency(pricing?.defaultSubFrequency)) {
      const defaultPlan = this.#getDefaultPlan(plans, pricing.defaultSubFrequency);
      this.selectedPlanId = defaultPlan ? defaultPlan.id : null;
    } else if (!this.selectedPlanId || !planIds.includes(String(this.selectedPlanId))) {
      this.selectedPlanId = plans[0] ? plans[0].id : null;
    }

    if (this.purchaseType === 'subscription' && plans.length > 0) {
      this.#renderFrequencySelect(radio);
    }

    this.#applyPricing(radio);
    this.#updateUpsell(radio.dataset.variantId);
  }

  #readUpsellData() {
    const wrap = this.section.querySelector('[data-lp-upsell]');
    if (!wrap) return null;
    const blockId = wrap.dataset.lpUpsellBlockId;
    const el = document.getElementById('LpUpsellData-' + blockId);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  #getUpsellConfig(variantId) {
    if (!this.upsellData || !this.upsellData.default) return null;
    const override =
      variantId && this.upsellData.variants ? this.upsellData.variants[String(variantId)] : null;
    return override || this.upsellData.default;
  }

  #updateUpsellButtonState(btn, config) {
    if (!btn || !config) return;
    const defaultLabel = config.buttonText || 'Add';
    btn.dataset.lpUpsellLabelDefault = defaultLabel;
    const isArmed =
      this.armedUpsellVariantId && String(this.armedUpsellVariantId) === String(config.variantId);
    const label = btn.querySelector('[data-lp-upsell-button-text]');
    if (label) label.textContent = isArmed ? 'Cancel' : defaultLabel;
    btn.classList.toggle('lp-upsell__button--armed', isArmed);
    btn.setAttribute('aria-pressed', isArmed ? 'true' : 'false');
  }

  #applyUpsellConfig(config) {
    const wrap = this.section.querySelector('[data-lp-upsell]');
    if (!wrap || !config) return;
    this.currentUpsellConfig = config;
    const panel = wrap.querySelector('.lp-upsell');
    const media = wrap.querySelector('.lp-upsell__media');
    const img = wrap.querySelector('.lp-upsell__image');
    const titleEl = wrap.querySelector('[data-lp-upsell-title]');
    const textEl = wrap.querySelector('[data-lp-upsell-text]');
    const btn = wrap.querySelector('[data-lp-upsell-atc]');
    if (!panel) return;

    panel.style.setProperty('--lp-upsell-border-color', config.borderColor || '#17162e');
    panel.style.setProperty('--lp-upsell-bg', config.backgroundColor || '#ffffff');
    panel.style.setProperty('--lp-upsell-text-color', config.textColor || '#17162e');
    panel.style.setProperty('--lp-upsell-btn-bg', config.buttonBackgroundColor || '#ffd700');
    panel.style.setProperty('--lp-upsell-btn-color', config.buttonTextColor || '#000000');

    if (media && img) {
      if (config.image) {
        img.src = config.image;
        img.alt = config.title || '';
        media.hidden = false;
      } else {
        media.hidden = true;
      }
    }

    if (titleEl) {
      titleEl.textContent = config.title || '';
      titleEl.hidden = !config.title;
    }

    if (textEl) {
      textEl.innerHTML = config.text || '';
      textEl.hidden = !config.text;
    }

    if (btn) {
      btn.dataset.variantId = config.variantId ? String(config.variantId) : '';
      btn.disabled = !config.available || !config.variantId;
      this.#updateUpsellButtonState(btn, config);
    }
  }

  #setUpsellVisible(visible) {
    const wrap = this.section.querySelector('[data-lp-upsell]');
    if (!wrap) return;
    wrap.classList.toggle('lp-upsell-wrap--visible', visible);
  }

  #updateUpsell(variantId) {
    const config = this.#getUpsellConfig(variantId);
    if (!config) return;
    if (
      this.armedUpsellVariantId &&
      String(this.armedUpsellVariantId) !== String(config.variantId)
    ) {
      this.armedUpsellVariantId = null;
    }
    this.#applyUpsellConfig(config);
    if (this.requireVariantSelection) this.#setUpsellVisible(!!variantId);
  }

  #toggleUpsellArm() {
    const config = this.currentUpsellConfig;
    const btn = this.section.querySelector('[data-lp-upsell-atc]');
    if (!config || !config.variantId || !btn || btn.disabled) return;

    const configVariantId = String(config.variantId);
    if (this.armedUpsellVariantId && String(this.armedUpsellVariantId) === configVariantId) {
      this.armedUpsellVariantId = null;
    } else {
      this.armedUpsellVariantId = configVariantId;
    }
    this.#updateUpsellButtonState(btn, config);
  }

  #initUpsell() {
    const wrap = this.section.querySelector('[data-lp-upsell]');
    if (!wrap) return;

    this.upsellData = this.#readUpsellData();
    if (!this.upsellData) return;

    const checked = this.#getCheckedBundleRadio();
    if (this.requireVariantSelection) {
      this.#setUpsellVisible(!!checked);
    } else {
      wrap.classList.add('lp-upsell-wrap--visible');
    }
    this.#updateUpsell(checked ? checked.dataset.variantId : null);

    wrap.querySelectorAll('[data-lp-upsell-atc]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.#toggleUpsellArm();
      });
    });
  }

  #onFrequencyChange() {
    const select = this.section.querySelector('[data-lp-frequency-select]');
    const radio = this.#getCheckedBundleRadio();
    if (!select || !radio) return;

    this.selectedPlanId = select.value;
    this.#applyPricing(radio);
  }

  #clearAtcState() {
    const atcButton = this.section.querySelector('.lp-atc-button');
    const atcPrice = this.section.querySelector('.lp-atc-price');
    if (atcButton) {
      delete atcButton.dataset.variantId;
      atcButton.dataset.quantity = '1';
      delete atcButton.dataset.giftVariantIds;
      delete atcButton.dataset.sellingPlanId;
    }
    if (atcPrice) {
      atcPrice.innerHTML = '';
      atcPrice.classList.add('hidden');
    }
  }

  #measureBundleItems() {
    this.section.querySelectorAll('.lp-bundle-item').forEach((item) => {
      item.style.setProperty('--lp-bundle-item-max-h', item.scrollHeight + 'px');
    });
  }

  #initBundlePickerAnimation() {
    if (!this.hideVariantsOnSelect) return;
    const picker = this.section.querySelector('.lp-variant-picker');
    if (!picker) return;
    picker.classList.add('lp-variant-picker--animate-items');
    this.#measureBundleItems();
  }

  #setPickerCollapsed(collapsed) {
    this.pickerCollapsed = collapsed;
    const picker = this.section.querySelector('.lp-variant-picker');
    if (picker) picker.classList.toggle('lp-variant-picker--collapsed', collapsed);
    this.section.querySelectorAll('.lp-bundle-item').forEach((item) => {
      const radio = item.querySelector('.lp-bundle-radio');
      item.classList.toggle('lp-bundle-item--selected', !!(radio && radio.checked));
    });
    if (this.hideVariantsOnSelect && !collapsed) this.#measureBundleItems();
  }

  #promptVariantSelection() {
    const picker =
      this.section.querySelector('.lp-variant-picker') ||
      this.section.querySelector('[data-lp-purchase-card]');
    if (!picker) return;
    const top = picker.getBoundingClientRect().top + window.pageYOffset - 180;
    window.scrollTo({ top, behavior: 'smooth' });
    picker.classList.remove('lp-variant-picker--attention');
    void picker.offsetWidth;
    picker.classList.add('lp-variant-picker--attention');
    window.setTimeout(() => picker.classList.remove('lp-variant-picker--attention'), 2400);
  }

  #hasVariantSelected() {
    return !!this.#getCheckedBundleRadio();
  }

  #updateOtpBuyButton(radio) {
    const btn = this.section.querySelector('.lp-subscription-otp-buy-button');
    if (!btn || !radio) return;

    const template = btn.getAttribute('data-text') || '';
    const qty = parseInt(radio.dataset.quantity, 10) || 1;
    let otpCents = 0;

    if (radio.closest('[data-lp-purchase-card]')) {
      // priceEach already reflects the full price of this option's own variant.
      const { priceEach } = this.#getPurchaseCardUnitPrices(radio);
      otpCents = priceEach;
    } else {
      const pricing = this.#getVariantPricing(radio.dataset.variantId);
      if (pricing && pricing.otp) {
        otpCents = pricing.otp.price;
      } else {
        otpCents = (parseInt(radio.dataset.originalPrice, 10) || parseInt(radio.dataset.price, 10) || 0) * qty;
      }
    }

    btn.textContent = template.replaceAll('[price]', this.#formatMoney(otpCents, false));
  }

  #applyPricing(radio) {
    if (!radio) return;
    const atcButton = this.section.querySelector('.lp-atc-button');
    const atcPrice = this.section.querySelector('.lp-atc-price');
    const shippingLabelEl = document.getElementById('shipping-label-' + this.sectionId);
    const active = this.#getActivePricing(radio);
    const qty = this.#getCartQuantity(radio);
    const totals = this.#getLineTotals(active, radio);

    if (atcButton) {
      const inPurchaseCard = !!radio.closest('[data-lp-purchase-card]');
      const giftIds =
        inPurchaseCard && this.purchaseType === 'onetime' ? '' : radio.dataset.giftVariantIds || '';
      const planId =
        this.purchaseType === 'subscription'
          ? active.planId || radio.dataset.sellingPlanId || ''
          : '';

      atcButton.dataset.variantId = radio.dataset.variantId;
      atcButton.dataset.quantity = qty;
      atcButton.dataset.giftVariantIds = giftIds;
      atcButton.dataset.sellingPlanId = planId;
      atcButton.disabled = false;
      const label = atcButton.querySelector('span:first-child');
      if (label) label.textContent = 'Add to cart';
    }

    if (atcPrice) {
      // Purchase card uses <s> for the crossed-out price; everywhere else keeps <del>.
      const strikeTag = radio.closest('[data-lp-purchase-card]') ? 's' : 'del';
      let html = '';
      if (totals.discounted < totals.original) {
        html += '<' + strikeTag + '>' + this.#formatMoney(totals.original, true) + '</' + strikeTag + '> ';
      }
      html += '<strong>' + this.#formatMoney(totals.discounted, false) + '</strong>';
      atcPrice.innerHTML = html;
      atcPrice.classList.remove('hidden');
    }

    if (shippingLabelEl) {
      const shippingHtml = '<strong>FREE</strong>&nbsp; Shipping over <strong>$75</strong>';
      shippingLabelEl.innerHTML = shippingHtml;
      const stickyShipEl = document.querySelector('.lp-sticky-shipping');
      if (stickyShipEl) stickyShipEl.innerHTML = shippingHtml;
    }

    this.#updateBundleCardPrices(radio);
    this.#updateOtpBuyButton(radio);
  }

  #initDeliveryGeo() {
    const deliverySpan = document.getElementById('delivery-info-' + this.sectionId);
    const flagImg = document.getElementById('country-flag-' + this.sectionId);
    const shippingLabel = document.getElementById('shipping-label-' + this.sectionId);
    const stickyDelivery = document.querySelector('.lp-sticky-delivery');
    const stickyFlag = document.querySelector('.lp-sticky-flag');
    const stickyShipping = document.querySelector('.lp-sticky-shipping');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const getDeliveryDate = (daysToAdd) => {
      const date = new Date();
      date.setDate(date.getDate() + daysToAdd);
      return months[date.getMonth()] + ' ' + date.getDate();
    };

    const updateDelivery = (countryCode) => {
      const isUS = countryCode && countryCode.toUpperCase() === 'US';
      const deliveryDays = isUS ? 5 : 10;
      const deliveryDate = getDeliveryDate(deliveryDays);
      const html = 'Delivers by <strong>' + deliveryDate + '</strong>';
      if (deliverySpan) deliverySpan.innerHTML = html;
      if (stickyDelivery) stickyDelivery.innerHTML = html;
    };

    updateDelivery('US');

    fetch('https://get.geojs.io/v1/ip/geo.json')
      .then((response) => response.json())
      .then((data) => {
        const countryCode = data.country_code || 'US';
        const flagUrl = 'https://flagcdn.com/w20/' + countryCode.toLowerCase() + '.png';
        const altText = (data.country || 'Country') + ' Flag';
        if (flagImg) {
          flagImg.src = flagUrl;
          flagImg.alt = altText;
        }
        if (stickyFlag) {
          stickyFlag.src = flagUrl;
          stickyFlag.alt = altText;
        }
        updateDelivery(countryCode);
        const shippingHtml = '<strong>FREE</strong>&nbsp; Shipping over <strong>$75</strong>';
        if (shippingLabel) shippingLabel.innerHTML = shippingHtml;
        if (stickyShipping) stickyShipping.innerHTML = shippingHtml;
      })
      .catch(() => updateDelivery('US'));
  }

  #initGallery() {
    const track = this.section.querySelector('#GalleryTrack-' + this.sectionId);
    const items = track ? Array.from(track.querySelectorAll('.lp-product-gallery__item')) : [];
    const dots = this.section.querySelectorAll('.lp-product-gallery__dot, .lp-product-gallery__thumb');
    const prevBtns = this.section.querySelectorAll('.lp-product-gallery__arrow--prev');
    const nextBtns = this.section.querySelectorAll('.lp-product-gallery__arrow--next');
    let currentSlide = 0;

    const updateGalleryButtonStates = () => {
      prevBtns.forEach((btn) => {
        const disabled = currentSlide === 0;
        btn.classList.toggle('is-disabled', disabled);
        btn.classList.toggle('is-active', !disabled);
        btn.toggleAttribute('disabled', disabled);
        btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      });
      nextBtns.forEach((btn) => {
        const disabled = currentSlide === items.length - 1;
        btn.classList.toggle('is-disabled', disabled);
        btn.classList.toggle('is-active', !disabled);
        btn.toggleAttribute('disabled', disabled);
        btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      });
    };

    const scrollActiveThumbIntoView = () => {
      const thumbsContainer = this.section.querySelector('.lp-product-gallery__thumbs');
      if (!thumbsContainer) return;
      const activeThumb = thumbsContainer.querySelector('.lp-product-gallery__thumb.active');
      if (!activeThumb) return;
      const containerRect = thumbsContainer.getBoundingClientRect();
      const thumbRect = activeThumb.getBoundingClientRect();
      const offsetLeft = thumbRect.left - containerRect.left + thumbsContainer.scrollLeft;
      const scrollTo = offsetLeft - containerRect.width / 2 + thumbRect.width / 2;
      thumbsContainer.scrollLeft = scrollTo;
    };

    const goToSlide = (index) => {
      if (items.length === 0) return;
      if (index < 0) index = 0;
      if (index >= items.length) index = items.length - 1;
      currentSlide = index;
      track.style.transform = 'translateX(-' + currentSlide * 100 + '%)';
      dots.forEach((dot) => {
        dot.classList.toggle('active', parseInt(dot.dataset.index, 10) === currentSlide);
      });
      updateGalleryButtonStates();
      scrollActiveThumbIntoView();
    };

    prevBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.hasAttribute('disabled')) return;
        goToSlide(currentSlide - 1);
      });
    });

    nextBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.hasAttribute('disabled')) return;
        goToSlide(currentSlide + 1);
      });
    });

    updateGalleryButtonStates();

    dots.forEach((dot) => {
      dot.addEventListener('click', () => goToSlide(parseInt(dot.dataset.index, 10)));
    });

    let pointerStartX = 0;
    let pointerStartY = 0;
    let isSwiping = false;
    const swipeThreshold = 40;

    const onPointerStart = (x, y) => {
      pointerStartX = x;
      pointerStartY = y;
      isSwiping = true;
    };

    const onPointerEnd = (x, y) => {
      if (!isSwiping) return;
      isSwiping = false;
      const diffX = pointerStartX - x;
      const diffY = Math.abs(pointerStartY - y);
      if (Math.abs(diffX) > swipeThreshold && Math.abs(diffX) > diffY) {
        goToSlide(diffX > 0 ? currentSlide + 1 : currentSlide - 1);
      }
    };

    if (track) {
      track.addEventListener(
        'touchstart',
        (e) => onPointerStart(e.changedTouches[0].screenX, e.changedTouches[0].screenY),
        { passive: true }
      );
      track.addEventListener(
        'touchmove',
        (e) => {
          if (!isSwiping) return;
          const diffX = Math.abs(e.changedTouches[0].screenX - pointerStartX);
          const diffY = Math.abs(e.changedTouches[0].screenY - pointerStartY);
          if (diffX > diffY && diffX > 10) e.preventDefault();
        },
        { passive: false }
      );
      track.addEventListener(
        'touchend',
        (e) => onPointerEnd(e.changedTouches[0].screenX, e.changedTouches[0].screenY),
        { passive: true }
      );
      track.addEventListener('mousedown', (e) => {
        onPointerStart(e.screenX, e.screenY);
        track.style.cursor = 'grabbing';
        e.preventDefault();
      });
      document.addEventListener('mousemove', (e) => {
        if (!isSwiping) return;
        e.preventDefault();
      });
      document.addEventListener('mouseup', (e) => {
        if (!isSwiping) return;
        track.style.cursor = '';
        onPointerEnd(e.screenX, e.screenY);
      });
      track.style.cursor = 'grab';
      track.addEventListener('dragstart', (e) => e.preventDefault());
    }
  }

  #initBundleSelection() {
    const bundleRadios = this.section.querySelectorAll('.lp-bundle-radio');
    bundleRadios.forEach((radio) => {
      const item = radio.closest('.lp-bundle-item');
      radio.addEventListener('change', () => {
        this.#onVariantChange(radio);
        if (this.hideVariantsOnSelect) this.#setPickerCollapsed(true);
      });
      if (this.hideVariantsOnSelect && item) {
        item.addEventListener('click', (e) => {
          if (e.target.closest('.lp-gift-row__info-trigger')) return;
          if (radio.checked && this.pickerCollapsed) this.#setPickerCollapsed(false);
        });
      }
    });

    this.#initBundlePickerAnimation();

    // The buy box has a single fixed plan — there's nothing to "select", so it's exempt
    // from the require-selection gate below (which assumes a real multi-option picker).
    const isBuyBox = !!this.section.querySelector('[data-lp-buybox]');

    if (this.requireVariantSelection && !isBuyBox) {
      bundleRadios.forEach((radio) => {
        radio.checked = false;
      });
      this.#setPickerCollapsed(false);
      this.#clearAtcState();
      return;
    }

    const checked = this.#getCheckedBundleRadio();
    if (checked) this.#onVariantChange(checked);
  }

  #setSubscriptionExtrasVisible(show) {
    const frequencyWrap = this.section.querySelector('[data-lp-frequency]');
    const benefits = this.section.querySelector('[data-lp-subscription-benefits]');
    if (frequencyWrap) {
      frequencyWrap.classList.remove('hidden');
      frequencyWrap.classList.toggle('is-collapsed', !show);
    }
    if (benefits) {
      benefits.classList.remove('hidden');
      benefits.classList.toggle('is-collapsed', !show);
    }
  }

  #initSubscription() {
    const toggle = this.section.querySelector('[data-lp-subscription-toggle]');
    const frequencySelect = this.section.querySelector('[data-lp-frequency-select]');
    if (!toggle) return;

    const options = toggle.querySelectorAll('[data-lp-purchase-type]');

    const setPurchaseType = (type) => {
      this.purchaseType = type;
      options.forEach((opt) => {
        const isActive = opt.dataset.lpPurchaseType === type;
        opt.classList.toggle('is-selected', isActive);
        opt.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      this.#setSubscriptionExtrasVisible(type === 'subscription');
      const buyboxSubChecks = this.section.querySelector('[data-lp-buybox-sub-checks]');
      const buyboxOtpChecks = this.section.querySelector('[data-lp-buybox-otp-checks]');
      if (buyboxSubChecks) buyboxSubChecks.classList.toggle('hidden', type !== 'subscription');
      if (buyboxOtpChecks) buyboxOtpChecks.classList.toggle('hidden', type !== 'onetime');
      const buyboxAtcSubtitle = this.section.querySelector('[data-lp-buybox-atc-subtitle]');
      if (buyboxAtcSubtitle) buyboxAtcSubtitle.classList.toggle('hidden', type !== 'subscription');
      const buyboxBenefits = this.section.querySelector('[data-lp-buybox-benefits]');
      if (buyboxBenefits) buyboxBenefits.classList.toggle('is-otp', type !== 'subscription');
      const checked = this.#getCheckedBundleRadio();
      if (type === 'onetime') {
        this.selectedPlanId = null;
      } else {
        const frequencyRadio = this.#getFrequencySourceRadio();
        if (frequencyRadio) {
          this.#renderFrequencySelect(frequencyRadio);
          if (!this.selectedPlanId && frequencyRadio.dataset.sellingPlanId) {
            this.selectedPlanId = frequencyRadio.dataset.sellingPlanId;
          }
        }
      }
      this.#updateAllBundleCardPrices();
      if (checked) this.#applyPricing(checked);
    };

    options.forEach((opt) => {
      opt.addEventListener('click', () => setPurchaseType(opt.dataset.lpPurchaseType));
      opt.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setPurchaseType(opt.dataset.lpPurchaseType);
        }
      });
    });

    if (frequencySelect) {
      frequencySelect.addEventListener('change', () => this.#onFrequencyChange());
    }

    setPurchaseType(this.purchaseType);
  }

  #initAddToCart() {
    const atcButton = this.section.querySelector('.lp-atc-button');
    if (atcButton) {
      if (atcButton.dataset.btnType === 'checkout') window.opusBlockInstantAddToCart = true;

      atcButton.addEventListener('click', (e) => {
        e.preventDefault();
        const btn = atcButton;
        if (this.requireVariantSelection && !this.#hasVariantSelected()) {
          this.#promptVariantSelection();
          return;
        }
        const variantId = btn.dataset.variantId;
        const qty = parseInt(btn.dataset.quantity, 10) || 1;
        const giftIds = btn.dataset.giftVariantIds || '';
        const sellingPlanId = btn.dataset.sellingPlanId || '';
        if (!variantId || btn.disabled) return;

        const isBundle = !!(giftIds && sellingPlanId);
        const bundleItemProperties = (quantitySelector = false, removable = false) => ({
          _bundleId:
            String(variantId).slice(-5, -1) +
            '-' +
            String(sellingPlanId).slice(-5, -1) +
            '-' +
            String(giftIds.split(',').filter(Boolean).length),
          _quantitySelector: quantitySelector ? 'true' : 'false',
          _removable: removable ? 'true' : 'false',
        });

        btn.disabled = true;
        const label = btn.querySelector('span:first-child');
        if (label) label.textContent = 'Adding...';

        const item = { id: parseInt(variantId, 10), quantity: qty };
        if (isBundle) item.properties = bundleItemProperties(true, true);
        if (sellingPlanId) item.selling_plan = parseInt(sellingPlanId, 10);

        const items = [item];
        if (giftIds) {
          giftIds.split(',').forEach((gid) => {
            const [id, quantity] = gid.trim().split(':');
            const giftId = parseInt(id, 10);
            if (!giftId) return;
            const giftItem = {
              id: giftId,
              quantity: parseInt(quantity, 10) || 1,
            };
            if (isBundle) giftItem.properties = bundleItemProperties();
            items.push(giftItem);
          });
        }
      if (this.armedUpsellVariantId) {
  const upsellId = parseInt(this.armedUpsellVariantId, 10);

  if (upsellId) {
    items.push({
      id: upsellId,
      quantity: 1,
      properties: {
        _serlino_cart_upsell: 'true'
      }
    });
  }
}

       const cartDrawer = document.querySelector('cart-drawer');

const sections =
  cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
    ? cartDrawer.getSectionsToRender().map((section) => section.id)
    : [];

fetch('/cart/add.js', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  body: JSON.stringify({
    items,
    sections,
    sections_url: window.location.pathname,
  }),
})
          .then(async (res) => {
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
              const msg = data.description || data.message || 'Could not add to cart';
              throw new Error(msg);
            }
            return data;
          })
         .then((data) => {
            if (btn.dataset.btnType === 'checkout') {
              window.location.href = '/checkout';
              return;
            }
 if (
  cartDrawer &&
  typeof cartDrawer.renderContents === 'function' &&
  data.sections
) {
  cartDrawer.renderContents(data);

 
  cartDrawer.classList.remove('is-empty');

  const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');

  if (cartDrawerItems) {
    cartDrawerItems.classList.remove('is-empty');
  }
}
            this.armedUpsellVariantId = null;
            const upsellBtn = this.section.querySelector('[data-lp-upsell-atc]');
            if (upsellBtn && this.currentUpsellConfig) {
              this.#updateUpsellButtonState(upsellBtn, this.currentUpsellConfig);
            }
            if (label) label.textContent = 'Added!';
            setTimeout(() => {
              btn.disabled = false;
              if (label) label.textContent = 'Add to cart';
            }, 1500);
            fetch('/cart.js')
  .then((r) => r.json())
  .then((cart) => {
    document
      .querySelectorAll('[data-cart-count], .cart-count-bubble span')
      .forEach((el) => {
        el.textContent = cart.item_count;
      });

    const checkoutTotal = document.querySelector(
      '#CartDrawer .serlino-checkout-total'
    );

    if (checkoutTotal) {
      checkoutTotal.textContent =
        this.moneySymbol + (cart.total_price / 100).toFixed(2);
    }
  });
          })
          .catch((err) => {
            console.error('[lp-atc]', err);
            if (label) label.textContent = 'Error – retry';
            btn.disabled = false;
          });
      });
    }

    const otpBtn = this.section.querySelector('.lp-subscription-otp-buy-button');
    if (!otpBtn) return;

    otpBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.requireVariantSelection && !this.#hasVariantSelected()) {
        this.#promptVariantSelection();
        return;
      }
      const radio = this.#getCheckedBundleRadio();
      const variantId = radio?.dataset.variantId || atcButton?.dataset.variantId;
      if (!variantId || otpBtn.disabled) return;

      const qty = radio ? this.#getCartQuantity(radio) : 1;
      otpBtn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty }] }),
      })
        .then((res) => res.json())
        .then(() => {
          if (atcButton?.dataset.btnType === 'checkout') {
            window.location.href = '/checkout';
            return;
          }
          if (radio) this.#updateOtpBuyButton(radio);
          otpBtn.disabled = false;
          fetch('/cart.js')
            .then((r) => r.json())
            .then((cart) => {
              document.querySelectorAll('[data-cart-count], .cart-count-bubble span').forEach((el) => {
                el.textContent = cart.item_count;
              });
            });
        })
        .catch(() => {
          otpBtn.disabled = false;
        });
    });
  }

  #initTestimonialVideos() {
    const allVideoCards = this.section.querySelectorAll('.lp-testimonial-card');

    const stopAllVideos = () => {
      allVideoCards.forEach((card) => {
        const v = card.querySelector('.lp-video-native');
        const o = card.querySelector('.lp-video-play-overlay');
        if (v && !v.paused) {
          v.pause();
          v.muted = true;
        }
        if (o) o.classList.remove('is-hidden');
      });
    };

    allVideoCards.forEach((card) => {
      const video = card.querySelector('.lp-video-native');
      const playOverlay = card.querySelector('.lp-video-play-overlay');
      const playSvg = playOverlay ? playOverlay.querySelector('svg') : null;
      if (!video) return;

      video.muted = true;
      video.loop = true;
      video.playsInline = true;

      if (playSvg) {
        playSvg.style.cursor = 'pointer';
        playSvg.addEventListener('click', (e) => {
          e.stopPropagation();
          stopAllVideos();
          video.muted = false;
          video.play().then(() => playOverlay.classList.add('is-hidden')).catch(() => { });
        });
      }

      video.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!video.paused) {
          video.pause();
          video.muted = true;
          if (playOverlay) playOverlay.classList.remove('is-hidden');
        }
      });
    });
  }

  #initQuickLinks() {
    this.section.querySelectorAll('.lp-pill--scroll').forEach((pill) => {
      pill.addEventListener('click', (e) => {
        const targetId = pill.getAttribute('href');
        if (!targetId || targetId === '#') return;
        const targetEl = document.querySelector(targetId);
        if (targetEl) {
          e.preventDefault();
          const stickyHeader =
            document.querySelector('sticky-header') || document.querySelector('.shopify-section-header');
          const headerOffset = stickyHeader ? stickyHeader.offsetHeight : 0;
          const elementPosition = targetEl.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: elementPosition - headerOffset - 16, behavior: 'smooth' });
        }
      });
    });
  }

  #initTestimonialsSlider() {
    const slider = this.section.querySelector('.lp-testimonials__slider');
    const scrollBar = this.section.querySelector('.lp-testimonials__scroll-bar');
    const thumb = this.section.querySelector('.lp-testimonials__scroll-thumb');
    if (!slider || !scrollBar || !thumb) return;

    const updateProgress = () => {
      const max = slider.scrollWidth - slider.clientWidth;
      if (max <= 0) {
        thumb.style.width = '100%';
        return;
      }
      const viewportRatio = slider.clientWidth / slider.scrollWidth;
      const scrollRatio = slider.scrollLeft / max;
      const pct = viewportRatio + scrollRatio * (1 - viewportRatio);
      thumb.style.width = pct * 100 + '%';
    };

    slider.addEventListener('scroll', updateProgress);
    window.addEventListener('resize', updateProgress);
    updateProgress();

    let sliderDragging = false;
    let sliderStartX = 0;
    let sliderScrollStart = 0;

    slider.style.cursor = 'grab';

    slider.addEventListener('mousedown', (e) => {
      sliderDragging = true;
      sliderStartX = e.pageX;
      sliderScrollStart = slider.scrollLeft;
      slider.style.cursor = 'grabbing';
      slider.style.scrollSnapType = 'none';
      slider.style.scrollBehavior = 'auto';
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!sliderDragging) return;
      slider.scrollLeft = sliderScrollStart - (e.pageX - sliderStartX);
    });

    document.addEventListener('mouseup', () => {
      if (!sliderDragging) return;
      sliderDragging = false;
      slider.style.cursor = 'grab';
      slider.style.scrollSnapType = '';
      slider.style.scrollBehavior = '';
    });

    slider.addEventListener('dragstart', (e) => e.preventDefault());
  }

  #initStickyAtc() {
    const stickyBar = document.getElementById('lp-sticky-atc');
    const mainAtcBtn = this.section.querySelector('.lp-atc-button');
    if (!stickyBar || !mainAtcBtn) return;

    const stickyObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        stickyBar.classList.toggle('is-visible', scrolledPast);
      });
    }, { threshold: 0 });

    stickyObserver.observe(mainAtcBtn);

    stickyBar.querySelector('.lp-sticky-atc__btn').addEventListener('click', () => {
      if (this.requireVariantSelection && !this.#hasVariantSelected()) {
        this.#promptVariantSelection();
        return;
      }
      const target = this.section.querySelector('.lp-product-info');
      if (target) {
        const header =
          document.querySelector('sticky-header') || document.querySelector('.shopify-section-header');
        const offset = header ? header.offsetHeight : 0;
        const top = target.getBoundingClientRect().top + window.pageYOffset - offset - 16;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-lp-night-cream-product]').forEach((section) => {
    const configEl = document.getElementById('LpNightCreamConfig-' + section.dataset.sectionId);
    let config = {};
    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent);
      } catch {
        config = {};
      }
    }
    new LpNightCreamProduct(section, config);
  });
});
